import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { http, apiError, DIVISIONS, CONDITIONS } from "../../lib/api";

const TEXT = [
  ["make", "Preferred make", "e.g. Peterbilt, CAT, Sea Ray"],
  ["model", "Preferred model", "e.g. 579, 320, Sundancer"],
  ["year_range", "Year range", "e.g. 2019 or newer"],
  ["budget", "Budget", "e.g. Up to $120,000"],
  ["location", "Preferred location", "e.g. Midwest, within 300 miles"],
  ["timeline", "Timeline", "e.g. Within 60 days"],
];

export const SourcingForm = ({ initialDivision = "", initialType = "", scrollOnDone = true }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", division: initialDivision, asset_type: initialType, make: "", model: "", year_range: "", budget: "", condition: "", location: "", timeline: "", requirements: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const div = DIVISIONS.find((d) => d.key === f.division);
  useEffect(() => {
    if (initialType) setF((p) => ({ ...p, division: initialDivision || p.division, asset_type: initialType }));
  }, [initialType, initialDivision]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/sourcing-requests", { ...f, division: div?.name || "" });
      setDone({ ref: data?.ref || "" });
      if (scrollOnDone) window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="border border-[rgba(212,175,55,0.35)] p-10 md:p-16 text-center" data-testid="sourcing-form-success">
        <CheckCircle2 className="w-12 h-12 text-gold mx-auto" />
        <p className="font-display text-4xl mt-5">Request received.</p>
        {done.ref && <p className="font-mono text-sm text-gold mt-3" data-testid="sourcing-success-ref">Reference {done.ref}</p>}
        <p className="text-slate-300 mt-4 max-w-lg mx-auto">Thank you. A confirmation has been sent to your email. Selby Asset Group will review your requirements and contact you with next steps. Sourcing is subject to market availability and confirmation.</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-10" data-testid="sourcing-form">
      <fieldset className="space-y-5">
        <legend className="eyebrow mb-5">01 — Contact</legend>
        <div className="grid sm:grid-cols-3 gap-5">
          <div><label className="label" htmlFor="sr-name">Name *</label><input id="sr-name" required className="field" value={f.name} onChange={set("name")} data-testid="sourcing-name-input" /></div>
          <div><label className="label" htmlFor="sr-email">Email *</label><input id="sr-email" type="email" required className="field" value={f.email} onChange={set("email")} data-testid="sourcing-email-input" /></div>
          <div><label className="label" htmlFor="sr-phone">Phone</label><input id="sr-phone" className="field" value={f.phone} onChange={set("phone")} data-testid="sourcing-phone-input" /></div>
        </div>
      </fieldset>
      <fieldset className="space-y-5">
        <legend className="eyebrow mb-5">02 — What are you looking for? <span className="text-slate-400 normal-case tracking-normal">(all optional)</span></legend>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="label" htmlFor="sr-div">Division</label>
            <select id="sr-div" className="field" value={f.division} onChange={(e) => setF({ ...f, division: e.target.value, asset_type: "" })} data-testid="sourcing-division-select">
              <option value="">Not sure / Any</option>
              {DIVISIONS.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="sr-type">Asset type</label>
            {div ? (
              <select id="sr-type" className="field" value={f.asset_type} onChange={set("asset_type")} data-testid="sourcing-type-select">
                <option value="">Any</option>
                {div.categories.map((c) => <option key={c}>{c}</option>)}
                {f.asset_type && !div.categories.includes(f.asset_type) && <option>{f.asset_type}</option>}
                <option>Other / not listed</option>
              </select>
            ) : (
              <input id="sr-type" className="field" value={f.asset_type} onChange={set("asset_type")} placeholder="e.g. Reefer trailer" data-testid="sourcing-type-input" />
            )}
          </div>
          <div>
            <label className="label" htmlFor="sr-cond">Condition</label>
            <select id="sr-cond" className="field" value={f.condition} onChange={set("condition")} data-testid="sourcing-condition-select">
              <option value="">Any</option>
              {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          {TEXT.map(([k, label, ph]) => (
            <div key={k}>
              <label className="label" htmlFor={`sr-${k}`}>{label}</label>
              <input id={`sr-${k}`} className="field" value={f[k]} onChange={set(k)} placeholder={ph} data-testid={`sourcing-${k.replace("_", "-")}-input`} />
            </div>
          ))}
        </div>
        <div>
          <label className="label" htmlFor="sr-req">Specifications & additional requirements</label>
          <textarea id="sr-req" rows={5} className="field resize-none" value={f.requirements} onChange={set("requirements")} placeholder="Specs, configuration, use case, anything that helps us search." data-testid="sourcing-requirements-input" />
        </div>
      </fieldset>
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set("website")} aria-hidden="true" />
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <button type="submit" disabled={busy} className="btn-gold" data-testid="sourcing-submit-button">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit Sourcing Request
        </button>
        <p className="text-xs text-slate-400 max-w-md">Submitting a request creates no obligation. All sourcing is subject to market availability and confirmation; no asset is guaranteed.</p>
      </div>
    </form>
  );
};
